import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Edit2, Trash2, X, Calendar, Sparkles, Upload, Loader2, FileSpreadsheet, Lock
} from 'lucide-react';
import { CateringProductPicker } from './CateringProductPicker';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_EXTRA = { corbalar: 3, ana_yemekler: 5, yardimci_yemekler: 3, salatalar: 3 };
const PICKER_GROUP = { corbalar: 'corba', ana_yemekler: 'ana_yemek', yardimci_yemekler: 'yardimci_yemek', salatalar: 'salata' };
const emptyItem = (cat) => ({ name: '', name_en: '', name_me: '', name_ru: '', name_sr: '', description: '', description_en: '', description_me: '', description_ru: '', description_sr: '', price_addition: DEFAULT_EXTRA[cat] ?? 3, image_url: '', stock: 50, code: '', unit: '' });

const CateringManagement = ({ token, onMenuSaved }) => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [translatingIndex, setTranslatingIndex] = useState(null);
  const [formData, setFormData] = useState({
    date: '', base_price: '10',
    corbalar: [emptyItem('corbalar')],
    ana_yemekler: [emptyItem('ana_yemekler')],
    yardimci_yemekler: [emptyItem('yardimci_yemekler')],
    salatalar: [emptyItem('salatalar')]
  });
  const [uploadingIndex, setUploadingIndex] = useState(null); // "corbalar-0" gibi

  const categoryLabels = { corbalar: 'Çorbalar', ana_yemekler: 'Ana Yemekler', yardimci_yemekler: 'Yardımcı Yemekler', salatalar: 'Salatalar' };
  const categoryApiKeys = { corbalar: 'corba', ana_yemekler: 'ana_yemek', yardimci_yemekler: 'yardimci_yemek', salatalar: 'salata' };

  const handleAiTranslate = async (categoryKey, index) => {
    const item = formData[categoryKey][index];
    if (!item.name.trim()) { toast.error('Önce Türkçe isim girin'); return; }
    
    setTranslatingIndex(`${categoryKey}-${index}`);
    try {
      const res = await axios.post(`${API}/admin/catering/translate`, {
        name: item.name,
        category: categoryApiKeys[categoryKey]
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const data = res.data;
      handleOptionChange(categoryKey, index, 'name_en', data.name_en || '');
      handleOptionChange(categoryKey, index, 'name_me', data.name_me || '');
      handleOptionChange(categoryKey, index, 'name_ru', data.name_ru || '');
      handleOptionChange(categoryKey, index, 'name_sr', data.name_sr || '');
      handleOptionChange(categoryKey, index, 'description', data.description || '');
      handleOptionChange(categoryKey, index, 'description_en', data.description_en || '');
      handleOptionChange(categoryKey, index, 'description_me', data.description_me || '');
      handleOptionChange(categoryKey, index, 'description_ru', data.description_ru || '');
      handleOptionChange(categoryKey, index, 'description_sr', data.description_sr || '');
      toast.success('AI çeviri tamamlandı (5 dil)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI çeviri başarısız');
    } finally {
      setTranslatingIndex(null);
    }
  };

  const [products, setProducts] = useState([]);
  const [productsMeta, setProductsMeta] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);

  useEffect(() => { fetchMenus(); fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/catering-products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data.items || []);
      setProductsMeta(res.data);
    } catch { toast.error('Ürün listesi yüklenemedi'); }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingExcel(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/catering-products/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Excel yüklenemedi');
    } finally { setUploadingExcel(false); }
  };

  const handlePickProduct = (cat, i, p) => setFormData(prev => ({
    ...prev, [cat]: prev[cat].map((item, idx) => idx === i ? {
      ...item, name: p.name, code: p.code, unit: p.unit || '',
      name_en: '', name_me: '', name_ru: '', name_sr: '',
      ...(p.price > 0 ? { price_addition: p.price } : {})
    } : item)
  }));

  const fetchMenus = async () => {
    try {
      const res = await axios.get(`${API}/admin/catering`, { headers: { Authorization: `Bearer ${token}` } });
      setMenus(res.data);
    } catch { toast.error('Catering menüleri yüklenemedi'); } finally { setLoading(false); }
  };

  const handleImageUpload = async (e, categoryKey, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingIndex(`${categoryKey}-${index}`);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      handleOptionChange(categoryKey, index, 'image_url', res.data.url);
      toast.success('Resim yüklendi');
    } catch (err) {
      toast.error('Resim yüklenemedi');
    } finally {
      setUploadingIndex(null);
    }
  };

  const resetForm = () => {
    setFormData({
      date: '', base_price: '10',
      corbalar: [emptyItem('corbalar')],
      ana_yemekler: [emptyItem('ana_yemekler')],
      yardimci_yemekler: [emptyItem('yardimci_yemekler')],
      salatalar: [emptyItem('salatalar')]
    });
    setEditingMenu(null);
  };

  const handleEdit = (menu) => {
    setEditingMenu(menu);
    const mapItem = (o) => ({ ...(o.id ? { id: o.id } : {}), name: o.name, name_en: o.name_en || '', name_me: o.name_me || '', name_ru: o.name_ru || '', name_sr: o.name_sr || '', description: o.description, description_en: o.description_en || '', description_me: o.description_me || '', description_ru: o.description_ru || '', description_sr: o.description_sr || '', price_addition: o.price_addition, image_url: o.image_url || '', stock: o.stock ?? 50, code: o.code || '', unit: o.unit || '' });
    setFormData({
      date: menu.date,
      base_price: menu.base_price.toString(),
      corbalar: menu.corbalar?.length ? menu.corbalar.map(mapItem) : [emptyItem('corbalar')],
      ana_yemekler: menu.ana_yemekler?.length ? menu.ana_yemekler.map(mapItem) : [emptyItem('ana_yemekler')],
      yardimci_yemekler: menu.yardimci_yemekler?.length ? menu.yardimci_yemekler.map(mapItem) : [emptyItem('yardimci_yemekler')],
      salatalar: menu.salatalar?.length ? menu.salatalar.map(mapItem) : [emptyItem('salatalar')]
    });
    setShowDialog(true);
  };

  const handleAddOption = (cat) => setFormData(p => ({ ...p, [cat]: [...p[cat], emptyItem(cat)] }));
  const handleRemoveOption = (cat, i) => setFormData(p => ({ ...p, [cat]: p[cat].filter((_, idx) => idx !== i) }));
  const handleOptionChange = (cat, i, field, val) => setFormData(p => ({ ...p, [cat]: p[cat].map((item, idx) => idx === i ? { ...item, [field]: val } : item) }));

  const handleSubmit = async () => {
    if (!formData.date || !formData.base_price) { toast.error('Tarih ve fiyat gerekli'); return; }
    try {
      const data = {
        ...formData, base_price: parseFloat(formData.base_price),
        corbalar: formData.corbalar.filter(o => o.name),
        ana_yemekler: formData.ana_yemekler.filter(o => o.name),
        yardimci_yemekler: formData.yardimci_yemekler.filter(o => o.name),
        salatalar: formData.salatalar.filter(o => o.name)
      };
      if (editingMenu) {
        await axios.put(`${API}/admin/catering/${editingMenu.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Menu guncellendi! Flyer hazir.', {
          action: { label: 'WhatsApp Paylas', onClick: () => onMenuSaved?.() },
          duration: 8000
        });
      } else {
        await axios.post(`${API}/admin/catering`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Menu olusturuldu! Flyer hazir.', {
          action: { label: 'WhatsApp Paylas', onClick: () => onMenuSaved?.() },
          duration: 8000
        });
      }
      setShowDialog(false); resetForm(); fetchMenus();
    } catch (e) { toast.error(e.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/catering/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Silindi'); fetchMenus();
    } catch { toast.error('Silinemedi'); }
  };

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-xl font-semibold">Catering Menü Yönetimi</h2>
        <div className="flex items-center gap-2 flex-wrap justify-end">
        <label className="flex items-center gap-2 px-4 py-2 border border-basil/40 rounded-full text-sm text-basil cursor-pointer hover:bg-basil/5 transition-colors" title={productsMeta?.source ? `Son yükleme: ${productsMeta.source}` : ''} data-testid="catering-products-upload-label">
          <input type="file" accept=".xlsx" onChange={handleExcelUpload} className="hidden" data-testid="catering-products-upload-input" />
          {uploadingExcel ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          <span>Ürün Listesi (<span data-testid="catering-products-count">{products.length}</span>) · Excel Yükle</span>
        </label>
        <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button className="bg-basil text-white hover:bg-basil/90 rounded-full"><Plus className="w-4 h-4 mr-2" />Yeni Menü</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingMenu ? 'Menü Düzenle' : 'Yeni Catering Menüsü'}</DialogTitle></DialogHeader>
            <div className="space-y-5 mt-4">
              <div className="bg-basil/10 border border-basil/20 rounded-lg p-3 mb-4">
                <p className="text-sm text-charcoal"><strong>Fiyatlandırma:</strong> Her kategoriden 1 adet = €10 baz fiyat. Aynı kategoriden ekstra seçimler ürün fiyatı ile eklenir.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Tarih</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="mt-1" disabled={!!editingMenu} /></div>
                <div><Label>Baz Fiyat (€)</Label><Input type="number" value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: e.target.value })} className="mt-1" placeholder="10" /></div>
              </div>
              <p className="text-xs text-charcoal/60 mt-1">* Ekstra Fiyat: Müşteri 4'ten fazla seçim yaparsa eklenen ücret</p>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <div key={key} className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold">{label}</Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => handleAddOption(key)}><Plus className="w-3 h-3 mr-1" />Ekle</Button>
                  </div>
                  <div className="space-y-3">
                    {formData[key].map((opt, i) => (
                      <div key={`${key}-${i}`} className="border rounded-lg p-3 bg-stone-50">
                        {products.length > 0 && !opt.code && (
                          <CateringProductPicker products={products} preferredGroup={PICKER_GROUP[key]} onSelect={(p) => handlePickProduct(key, i, p)} testId={`product-picker-${key}-${i}`} />
                        )}
                        {opt.code && (
                          <div className="flex items-center gap-2 mb-2 text-xs" data-testid={`product-code-badge-${key}-${i}`}>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-basil text-white font-mono"><Lock className="w-3 h-3" />{opt.code}</span>
                            <span className="text-charcoal/60">Listeden seçildi{opt.unit ? ` · ${opt.unit}` : ''}</span>
                            <button type="button" onClick={() => setFormData(p => ({ ...p, [key]: p[key].map((it, idx) => idx === i ? { ...it, code: '', unit: '' } : it) }))} className="ml-auto text-charcoal/50 hover:text-red-500 underline" data-testid={`product-unlink-${key}-${i}`}>Bağlantıyı kaldır</button>
                          </div>
                        )}
                        <div className="flex gap-2 items-center mb-2">
                          <Input placeholder={products.length ? 'Listede yoksa elle yazın (Türkçe)' : 'İsim (Türkçe)'} value={opt.name} readOnly={!!opt.code} onChange={(e) => handleOptionChange(key, i, 'name', e.target.value)} className={`flex-1 ${opt.code ? 'bg-basil/5 font-medium' : ''}`} data-testid={`catering-name-input-${key}-${i}`} />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={translatingIndex === `${key}-${i}` || !opt.name.trim()}
                            onClick={() => handleAiTranslate(key, i)}
                            className="whitespace-nowrap text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0 hover:from-blue-600 hover:to-purple-600 disabled:opacity-50"
                            data-testid={`ai-translate-btn-${key}-${i}`}
                          >
                            {translatingIndex === `${key}-${i}` ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                            AI Çevir
                          </Button>
                          <button type="button" onClick={() => handleRemoveOption(key, i)} className="p-2 text-red-500 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
                        </div>
                        {(opt.name_en || opt.name_me) && (
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input placeholder="Name (English)" value={opt.name_en || ''} onChange={(e) => handleOptionChange(key, i, 'name_en', e.target.value)} className="text-xs" />
                            <Input placeholder="Ime (Crnogorski)" value={opt.name_me || ''} onChange={(e) => handleOptionChange(key, i, 'name_me', e.target.value)} className="text-xs" />
                          </div>
                        )}
                        {(opt.name_ru || opt.name_sr) && (
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input placeholder="Имя (Русский)" value={opt.name_ru || ''} onChange={(e) => handleOptionChange(key, i, 'name_ru', e.target.value)} className="text-xs" data-testid={`cat-name-ru-${key}-${i}`} />
                            <Input placeholder="Име (Српски)" value={opt.name_sr || ''} onChange={(e) => handleOptionChange(key, i, 'name_sr', e.target.value)} className="text-xs" data-testid={`cat-name-sr-${key}-${i}`} />
                          </div>
                        )}
                        <div className="mb-2">
                          <Input placeholder="Açıklama (İçindekiler)" value={opt.description} onChange={(e) => handleOptionChange(key, i, 'description', e.target.value)} className="text-xs" />
                        </div>
                        {(opt.description_en || opt.description_me) && (
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input placeholder="Description (EN)" value={opt.description_en || ''} onChange={(e) => handleOptionChange(key, i, 'description_en', e.target.value)} className="text-xs" />
                            <Input placeholder="Opis (ME)" value={opt.description_me || ''} onChange={(e) => handleOptionChange(key, i, 'description_me', e.target.value)} className="text-xs" />
                          </div>
                        )}
                        {(opt.description_ru || opt.description_sr) && (
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input placeholder="Описание (RU)" value={opt.description_ru || ''} onChange={(e) => handleOptionChange(key, i, 'description_ru', e.target.value)} className="text-xs" data-testid={`cat-desc-ru-${key}-${i}`} />
                            <Input placeholder="Опис (SR)" value={opt.description_sr || ''} onChange={(e) => handleOptionChange(key, i, 'description_sr', e.target.value)} className="text-xs" data-testid={`cat-desc-sr-${key}-${i}`} />
                          </div>
                        )}
                        <div className="flex gap-2 items-center mb-2 text-xs text-charcoal/60">
                          <span className="w-24">Ekstra €</span><span className="w-24">Stok</span>
                        </div>
                        <div className="flex gap-2 items-center mb-2">
                          <Input type="number" placeholder="Ekstra €" value={opt.price_addition} onChange={(e) => handleOptionChange(key, i, 'price_addition', parseFloat(e.target.value) || 0)} className="w-24" />
                          <Input type="number" placeholder="Stok" value={opt.stock} onChange={(e) => handleOptionChange(key, i, 'stock', parseInt(e.target.value) || 0)} className="w-24" />
                        </div>
                        <div className="flex items-center gap-2">
                          {opt.image_url && (
                            <div className="relative w-16 h-16 rounded overflow-hidden bg-white border">
                              <img src={opt.image_url.startsWith('/api') ? `${API.replace('/api', '')}${opt.image_url}` : opt.image_url} alt="" className="w-full h-full object-cover" />
                              <button type="button" onClick={() => handleOptionChange(key, i, 'image_url', '')} className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, key, i)} className="hidden" />
                            {uploadingIndex === `${key}-${i}` ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Yükleniyor...</span></>
                            ) : (
                              <><Upload className="w-4 h-4" /><span className="text-sm">{opt.image_url ? 'Değiştir' : 'Resim Ekle'}</span></>
                            )}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Button onClick={handleSubmit} className="w-full bg-terracotta text-white hover:bg-terracotta/90">{editingMenu ? 'Güncelle' : 'Oluştur'}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="space-y-4">
        {menus.map((menu) => (
          <div key={menu.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-terracotta" />
                <span className="font-semibold">{format(new Date(menu.date), 'd MMMM yyyy, EEEE', { locale: tr })}</span>
                <span className="px-3 py-1 bg-basil/10 text-basil rounded-full text-sm font-medium">€{menu.base_price}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(menu)} className="p-2 hover:bg-stone-100 rounded-lg"><Edit2 className="w-4 h-4 text-charcoal/60" /></button>
                <button onClick={() => handleDelete(menu.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div><span className="text-charcoal/60">Çorba: </span>{menu.corbalar?.map(o => `${o.name} (${o.stock ?? '-'})`).join(', ') || '-'}</div>
              <div><span className="text-charcoal/60">Ana: </span>{menu.ana_yemekler?.map(o => `${o.name} (${o.stock ?? '-'})`).join(', ') || '-'}</div>
              <div><span className="text-charcoal/60">Yardımcı: </span>{menu.yardimci_yemekler?.map(o => `${o.name} (${o.stock ?? '-'})`).join(', ') || '-'}</div>
              <div><span className="text-charcoal/60">Salata: </span>{menu.salatalar?.map(o => `${o.name} (${o.stock ?? '-'})`).join(', ') || '-'}</div>
            </div>
          </div>
        ))}
      </div>
      {menus.length === 0 && <div className="text-center py-12 text-charcoal/60">Henüz catering menüsü yok</div>}
    </motion.div>
  );
};

export default CateringManagement;
